import { useCallback } from 'react';
import { useUrlFilters } from '@/lib/hooks/useUrlFilters';
import type { TaskFilters, TaskStatus, TaskPriority } from '../types';

const defaults: TaskFilters = {
  id: undefined,
  s: undefined,
  limit: 10,
  page: 0,
  orderBy: 'updatedAt',
  order: 'desc',
  'createdAt.min': undefined,
  'createdAt.max': undefined,
  'dueAt.min': undefined,
  'dueAt.max': undefined,
  periodId: undefined,
  clientId: undefined,
  createdById: undefined,
  matterId: undefined,
  assignedStaffId: undefined,
  typeId: undefined,
  status: 'NEW' as TaskStatus,
  priority: undefined,
};

const numberFields: (keyof TaskFilters)[] = [
  'id', 'limit', 'page', 'periodId', 'clientId', 'createdById',
  'matterId', 'assignedStaffId', 'typeId',
];

export function useNewTaskFilters(options?: { preserveAssignedStaffId?: boolean }) {
  const {
    updateFilter,
    updateFilters: baseUpdateFilters,
    setFilters,
    ...rest
  } = useUrlFilters<TaskFilters>({ defaults, numberFields });

  // Override updateFilters to default resetPage to false (generic defaults to true)
  const updateFilters = useCallback(
    (updates: Partial<TaskFilters>, resetPage = false) => baseUpdateFilters(updates, resetPage),
    [baseUpdateFilters]
  );

  const resetFilters = useCallback(() => {
    const preserved = options?.preserveAssignedStaffId !== false
      ? { assignedStaffId: rest.filters.assignedStaffId }
      : {};
    setFilters({ ...defaults, ...preserved });
  }, [setFilters, rest.filters.assignedStaffId, options?.preserveAssignedStaffId]);

  const setStatus = useCallback(
    (status: TaskStatus | undefined) => updateFilter('status', status, true),
    [updateFilter]
  );

  const setPriority = useCallback(
    (priority: TaskPriority | undefined) => updateFilter('priority', priority, true),
    [updateFilter]
  );

  const setSearch = useCallback(
    (search: string) => updateFilter('s', search || undefined, true),
    [updateFilter]
  );

  return { ...rest, updateFilter, updateFilters, resetFilters, setStatus, setPriority, setSearch };
}
